import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '../hooks/use-toast';
import { useConnectivity } from './ConnectivityContext';
import db from '../services/localDb';
import { clearAllLocalData, getLastSync } from '../services/localDb';
import { fullSync, deltaPull } from '../services/syncService';
import { getPendingCount, hasLocalData } from '../services/offlineApi';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const { isConnected, connectionType, isWifi, isCellular } = useConnectivity();

  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSyncState] = useState(null);
  const [hasOfflineData, setHasOfflineData] = useState(false);
  const syncInProgress = useRef(false);
  const prevConnected = useRef(isConnected);

  // ── Helpers ────────────────────────────────────────────
  const refreshPendingCount = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  const checkHasLocalData = useCallback(async () => {
    const has = await hasLocalData();
    setHasOfflineData(has);
  }, []);

  const loadLastSync = useCallback(async () => {
    const ts = await getLastSync('users');
    if (ts) setLastSyncState(new Date(ts));
  }, []);

  // ── Full sync (push + delta pull in one request) ───────
  const syncNow = useCallback(async () => {
    if (syncInProgress.current || !isConnected) return;
    syncInProgress.current = true;
    setIsSyncing(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const result = await fullSync(token);

      setLastSyncState(new Date());
      await refreshPendingCount();
      await checkHasLocalData();

      if (result.synced > 0 || result.conflicts > 0) {
        const parts = [];
        if (result.synced > 0) parts.push(`${result.synced} synchronisee(s)`);
        if (result.conflicts > 0) parts.push(`${result.conflicts} conflit(s) resolus`);
        toast({
          title: 'Synchronisation terminee',
          description: parts.join(', '),
        });
      }
    } catch (error) {
      console.error('[Sync] Error:', error);
    } finally {
      setIsSyncing(false);
      syncInProgress.current = false;
    }
  }, [isConnected, refreshPendingCount, checkHasLocalData]);

  // ── React to connection changes ────────────────────────
  useEffect(() => {
    if (isConnected && !prevConnected.current) {
      toast({ title: 'Connexion retablie', description: 'Synchronisation en cours...' });
      syncNow();
    }
    if (!isConnected && prevConnected.current) {
      toast({ title: 'Mode hors ligne', description: 'Vos donnees sont sauvegardees localement', variant: 'destructive' });
    }
    prevConnected.current = isConnected;
  }, [isConnected, syncNow]);

  // ── Init on mount ──────────────────────────────────────
  useEffect(() => {
    refreshPendingCount();
    checkHasLocalData();
    loadLastSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Pre-cache after login (first sync = full pull) ─────
  const preCacheContent = useCallback(async (token) => {
    if (!isConnected || !token) return;
    try {
      await deltaPull(token);
      await refreshPendingCount();
      await checkHasLocalData();
      setLastSyncState(new Date());
      console.log('[Offline] Pre-cache complete via delta pull');
    } catch (error) {
      console.warn('[Offline] Pre-cache partial failure:', error.message);
    }
  }, [isConnected, refreshPendingCount, checkHasLocalData]);

  // ── Backward-compat helpers ────────────────────────────
  const cacheData = useCallback(async (key, data) => {
    try {
      await db.syncMeta.put({ key: `cache_${key}`, data, syncedAt: new Date().toISOString() });
    } catch (e) { /* ignore */ }
  }, []);

  const getCachedData = useCallback(async (key) => {
    try {
      const meta = await db.syncMeta.get(`cache_${key}`);
      if (meta && meta.data) {
        const syncedAt = meta.syncedAt ? new Date(meta.syncedAt) : new Date(0);
        const ageMs = Date.now() - syncedAt.getTime();
        const isStale = ageMs > 5 * 60 * 1000; // stale after 5 min
        return { data: meta.data, isStale };
      }
    } catch (e) { /* ignore */ }
    return null;
  }, []);

  const queueAction = useCallback(async (action) => {
    const now = new Date().toISOString();
    await db.pendingActions.add({
      type: action.method || 'POST',
      endpoint: action.url?.replace(process.env.REACT_APP_BACKEND_URL || '', '').replace('/api', '') || action.endpoint,
      body: action.body || null,
      token: localStorage.getItem('token'),
      queuedAt: now,
      updatedAt: now,
      retries: 0,
    });
    await refreshPendingCount();
    return Date.now().toString();
  }, [refreshPendingCount]);

  const clearCache = useCallback(async () => {
    await clearAllLocalData();
    setPendingCount(0);
    setHasOfflineData(false);
    setLastSyncState(null);
  }, []);

  return (
    <OfflineContext.Provider
      value={{
        isOnline: isConnected,
        connectionType,
        isWifi,
        isCellular,
        pendingActions: [],
        pendingCount,
        isSyncing,
        lastSync,
        hasOfflineData,
        syncNow,
        preCacheContent,
        cacheData,
        getCachedData,
        queueAction,
        syncPendingActions: syncNow,
        clearCache,
        localDb: db,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
};
